import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { clearAuthData, setTokens, login, verifyTwoFactor } from './slices/authSlice'
import * as tokenStorage from '@/services/tokenStorage'
import { initEcho, disconnectEcho } from '@/services/echoClient'

/**
 * Side effects that used to live INSIDE authSlice reducers. Reducers must
 * stay pure — a reducer running twice (StrictMode, redux-persist rehydrate)
 * used to tear down/rebuild the Echo socket nondeterministically (and the
 * tokenStorage writes were non-deterministic too).
 *
 * Effects run AFTER the reducer, so by the time initEcho() reads the token
 * store it has already been persisted here.
 */
export const listenerMiddleware = createListenerMiddleware()

// Session established (full login, 2FA verification, or setTokens from a
// token refresh) → persist the credentials, then (re)bootstrap realtime.
// Persistence previously lived inside the reducers (setTokens, login.fulfilled,
// verifyTwoFactor.fulfilled); it happens here so those reducers stay pure.
listenerMiddleware.startListening({
  matcher: isAnyOf(setTokens, login.fulfilled, verifyTwoFactor.fulfilled),
  effect: (action: any) => {
    const payload: any = action.payload
    if (payload?.accessToken) {
      tokenStorage.setAccessToken(payload.accessToken)
      if (payload?.expiresIn) {
        tokenStorage.setTokenExpiry(Date.now() + Number(payload.expiresIn) * 1000)
      }
      if (payload?.tenantId) {
        tokenStorage.setTenantId(payload.tenantId)
      }
    }
    // Pending 2FA: login.fulfilled deliberately keeps only a provisional
    // token — the session (and realtime) is not established until
    // verifyTwoFactor succeeds.
    if (login.fulfilled.match(action) && !!action.payload?.twoFactorRequired) return
    initEcho()
  },
})

// Failed 2FA challenge: the provisional token persisted by login.fulfilled is
// no longer valid for anything — wipe storage alongside the reducer resetting
// state.accessToken.
listenerMiddleware.startListening({
  matcher: verifyTwoFactor.rejected.match,
  effect: () => {
    tokenStorage.clearAuthData()
  },
})

// Session torn down (logout/logoutAdmin dispatch clearAuthData) → wipe the
// token storage and drop the socket before the next login rebuilds it.
listenerMiddleware.startListening({
  matcher: isAnyOf(clearAuthData),
  effect: () => {
    tokenStorage.clearAuthData()
    disconnectEcho()
  },
})

export default listenerMiddleware
